import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Header.css';
import {
  ArchiveBoxIcon,
  ArrowLeftOnSquareIcon,
  BuildingIcon,
  ChartBarIcon,
  ChartTrendIcon,
  ClipboardCheckIcon,
  ClipboardIcon,
  CogIcon,
  CreditCardIcon,
  DownloadTrayIcon,
  EnvelopeIcon,
  FolderIcon,
  HomeIcon,
  IconProps,
  IdCardIcon,
  InboxIcon,
  MagnetIcon,
  PhoneIcon,
  PlusIcon,
  SearchIcon,
  SparklesIcon
} from './icons/HeroIcons';

type IconComponent = React.ComponentType<IconProps>;

type NavItem = {
  label: string;
  path?: string;
  icon: IconComponent;
  adminOnly?: boolean;
  requiresLenderRole?: boolean;
  requiresPaymentsRole?: boolean;
  requiresCurationRole?: boolean;
  requiresArchiveRole?: boolean;
  action?: () => void;
};

type NavSection = {
  id: string;
  label: string;
  icon: IconComponent;
  items: NavItem[];
  adminOnly?: boolean;
  requiresLenderRole?: boolean;
  requiresPaymentsRole?: boolean;
  requiresCurationRole?: boolean;
  requiresArchiveRole?: boolean;
};

const NAV_SECTIONS: NavSection[] = [
  {
    id: 'home',
    label: 'Home',
    icon: HomeIcon,
    items: [
      { label: 'Home', path: '/', icon: HomeIcon },
    ],
  },
  {
    id: 'email-activity',
    label: 'Email Activity',
    icon: EnvelopeIcon,
    items: [
      { label: 'Email Activity', path: '/email-activity', icon: EnvelopeIcon },
      { label: 'FC Search', path: '/complex-claims', icon: SearchIcon },
    ],
  },
  {
    id: 'caf-inbox',
    label: 'CAF Inbox',
    icon: InboxIcon,
    items: [{ label: 'CAF Inbox', path: '/caf-inbox', icon: InboxIcon }],
  },
  {
    id: 'lenders',
    label: 'Lenders',
    icon: BuildingIcon,
    requiresLenderRole: true,
    items: [
      { label: 'Lender hub', path: '/admin/lenders', icon: BuildingIcon, requiresLenderRole: true },
      // adminOnly: the lender-export API is admin-gated, so lender-managers
      // would only see 403s
      { label: 'Lender export', path: '/admin/lender-export', icon: DownloadTrayIcon, adminOnly: true },
    ],
  },
  {
    id: 'dashboards',
    label: 'Dashboards',
    icon: ChartBarIcon,
    items: [
      { label: 'Claim & user stats', path: '/dashboard', icon: ChartTrendIcon },
      { label: 'Audit analysis', path: '/audit-analysis', icon: ClipboardCheckIcon, adminOnly: true },
      { label: 'Redress', path: '/admin/redress-dashboard', icon: ChartBarIcon, adminOnly: true },
    ],
  },
  {
    id: 'tasks',
    label: 'Tasks',
    icon: FolderIcon,
    items: [
      { label: 'Emails', path: '/tasks', icon: EnvelopeIcon },
      { label: 'ID verification', path: '/id-verification', icon: IdCardIcon },
      { label: 'Post tasks', path: '/post-tasks', icon: InboxIcon },
      { label: 'Call tasks', path: '/call-tasks', icon: PhoneIcon, adminOnly: true },
      { label: 'Archived Tasks', path: '/archived-tasks', icon: ArchiveBoxIcon, requiresArchiveRole: true },
    ],
  },
  {
    id: 'leads',
    label: 'Leads',
    icon: MagnetIcon,
    items: [
      { label: 'Leads', path: '/leads', icon: MagnetIcon },
    ],
  },
  {
    id: 'credit-checks',
    label: 'Credit Checks',
    icon: SearchIcon,
    items: [
      { label: 'Credit Check Lookup', path: '/credit-checks', icon: SearchIcon },
    ],
  },
  {
    id: 'ai',
    label: 'Complex AI',
    icon: SparklesIcon,
    items: [
      { label: 'AI Assistant', path: '/ai-chat', icon: SparklesIcon },
    ],
  },
  {
    id: 'ai-curation',
    label: 'AI Curation',
    icon: SparklesIcon,
    requiresCurationRole: true,
    items: [
      { label: 'AI Curation', path: '/ai-curation', icon: SparklesIcon, requiresCurationRole: true },
    ],
  },
  {
    id: 'downloads',
    label: 'Downloads',
    icon: DownloadTrayIcon,
    items: [
      { label: 'Export clients for call', path: 'action:export-clients', icon: DownloadTrayIcon },
    ],
  },
  {
    id: 'payments',
    label: 'Payments',
    icon: CreditCardIcon,
    requiresPaymentsRole: true,
    items: [
      { label: 'Payments', path: '/admin/cancellation-payments', icon: CreditCardIcon, requiresPaymentsRole: true },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    icon: CogIcon,
    adminOnly: true,
    items: [
      { label: 'Admin overview', path: '/admin', icon: ClipboardIcon },
    ],
  },
];

const Header: React.FC = () => {
  const { isAuthenticated, isAdmin, hasRole, logout, token } = useAuth();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set());
  const [isExporting, setIsExporting] = useState(false);
  const canAccessLenders = isAdmin || hasRole('lender-manager');
  const canAccessPayments = isAdmin || hasRole('payments-manager');
  const canAccessCuration = isAdmin || hasRole('corpus-curator') || hasRole('workflow-publisher');
  const canAccessArchive = isAdmin || hasRole('archive-viewer');

  const sections = useMemo(() => {
    return NAV_SECTIONS
      .map(section => {
        if (section.adminOnly && !isAdmin) return null;
        if (section.requiresLenderRole && !canAccessLenders) return null;
        if (section.requiresPaymentsRole && !canAccessPayments) return null;
        if (section.requiresCurationRole && !canAccessCuration) return null;
        if (section.requiresArchiveRole && !canAccessArchive) return null;

        const items = section.items.filter(item => {
          if (item.adminOnly && !isAdmin) return false;
          if (item.requiresLenderRole && !canAccessLenders) return false;
          if (item.requiresPaymentsRole && !canAccessPayments) return false;
          if (item.requiresCurationRole && !canAccessCuration) return false;
          if (item.requiresArchiveRole && !canAccessArchive) return false;
          return true;
        });

        if (items.length === 0) return null;
        return { ...section, items };
      })
      .filter((section): section is NavSection => Boolean(section));
  }, [isAdmin, canAccessLenders, canAccessPayments, canAccessCuration, canAccessArchive]);

  useEffect(() => {
    setIsMobileOpen(false);
    setExpandedSections(new Set());
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  const toggleSection = (id: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.clear();
        next.add(id);
      }
      return next;
    });
  };

  const handleLogout = () => {
    logout();
    setIsMobileOpen(false);
  };

  const handleExportClientsForCall = async () => {
    setIsExporting(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${apiUrl}/ui/admin/export-clients-for-call`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to export clients');
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clients_for_call_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert('Clients exported successfully!');
    } catch (error) {
      console.error('Error exporting clients:', error);
      alert(`Failed to export clients: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const renderNavLink = (item: NavItem) => {
    const Icon = item.icon;
    // Check if this is an action item (not a navigation link)
    if (item.path?.startsWith('action:')) {
      const actionType = item.path.split(':')[1];

      return (
        <button
          key={item.path}
          className="sidebar-link sidebar-action-link"
          onClick={() => {
            setIsMobileOpen(false);
            if (actionType === 'export-clients') {
              handleExportClientsForCall();
            }
          }}
          disabled={actionType === 'export-clients' && isExporting}
        >
          <span className="link-icon inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-white/10 text-white">
            <Icon />
          </span>
          <span className="link-label">
            {actionType === 'export-clients' && isExporting ? 'Exporting...' : item.label}
          </span>
        </button>
      );
    }

    // Regular navigation link
    return (
      <NavLink
        key={item.path}
        to={item.path || '/'}
        end={item.path === '/'}
        className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
        onClick={() => setIsMobileOpen(false)}
      >
        <span className="link-icon inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-white/10 text-white/80">
          <Icon />
        </span>
        <span className="link-label">{item.label}</span>
      </NavLink>
    );
  };

  return (
    <>
      <button
        className="sidebar-mobile-trigger"
        onClick={() => setIsMobileOpen(true)}
        aria-label="Open navigation"
        aria-expanded={isMobileOpen}
      >
        ☰
      </button>
      {isMobileOpen && <div className="sidebar-backdrop" onClick={() => setIsMobileOpen(false)} />}

      <aside className={`app-sidebar ${isMobileOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <img src="/assets/logo.png" alt="ComplexLaw Logo" className="logo" />
          </div>
          <div className="sidebar-title">
            <h1>PCP Claims</h1>
            <span>Management System</span>
          </div>
          <button
            className="sidebar-close"
            onClick={() => setIsMobileOpen(false)}
            aria-label="Close navigation"
          >
            ×
          </button>
        </div>

        {isAuthenticated && (
          <Link
            to="/create-claim"
            className="create-claim-btn"
            onClick={() => setIsMobileOpen(false)}
          >
            <span className="create-claim-icon">
              <PlusIcon />
            </span>
            <span>Create Claim</span>
          </Link>
        )}

        <nav className="sidebar-nav">
          {sections.map(section => {
            const SectionIcon = section.icon;
            if ((section.id === 'home' || section.id === 'caf-inbox') && section.items.length === 1) {
              const item = section.items[0];
              return (
                <NavLink
                  key={section.id}
                  to={item.path || '/'}
                  end
                  className={({ isActive }) => `nav-single ${isActive ? 'active' : ''}`}
                  onClick={() => setIsMobileOpen(false)}
                >
                  <span className="section-icon inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-white">
                    <SectionIcon />
                  </span>
                  <div className="nav-single-text">
                    <span className="nav-single-label">{item.label}</span>
                  </div>
                  <span className="nav-single-arrow">→</span>
                </NavLink>
              );
            }

            const isExpanded = expandedSections.has(section.id);
            return (
              <div
                key={section.id}
                className={`nav-section ${isExpanded ? 'expanded' : ''}`}
              >
                <button
                  type="button"
                  className="nav-section-trigger"
                  onClick={() => toggleSection(section.id)}
                  aria-expanded={isExpanded}
                >
                  <span className="section-icon inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 text-white">
                    <SectionIcon />
                  </span>
                  <span className="section-label">{section.label}</span>
                  <span className="section-chevron">{isExpanded ? '▾' : '▸'}</span>
                </button>

                {isExpanded && (
                  <div className="nav-links-inline">
                    {section.items.map(renderNavLink)}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {isAuthenticated && (
          <button onClick={handleLogout} className="sidebar-logout">
            <span className="logout-icon inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white">
              <ArrowLeftOnSquareIcon />
            </span>
            <span>Logout</span>
          </button>
        )}
      </aside>
    </>
  );
};

export default Header;
